import React from "react";
import { ModeEditOutlineTwoTone, Visibility } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { toast } from "react-toastify";
import AppTable from "components/Shared/AppTable";
import FormDrawer from "components/Shared/FormDrawer";
import { useVehicleDetailContext } from "contexts/VehicleDetailContext";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useTablePagination } from "hooks";
import {
  useLazyListVehicleRegistrationsQuery,
  useUpdateVehicleRegistrationMutation,
} from "store/services/vehicleApi";
import VehicleRegistrationForm from "./VehicleRegistrationForm";
import { localizeDateShort } from "utils/dateFormats";

const VehicleRegistrationList = () => {
  const [showRegistrationForm, setShowRegistrationForm] = React.useState(false);

  const [selectedRegistration, setSelectedRegistration] = React.useState(null);

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const { vehicleDetail } = useVehicleDetailContext();

  const columns = [
    {
      name: "Vehicle Number",
      selector: (row) => row?.vehicleNumber,
      cell: (row) => row?.vehicleNumber || "N/A",
    },
    {
      name: "Document Type",
      selector: (row) => row?.documentName,
      cell: (row) => row?.documentName || "N/A",
    },
    {
      name: "Owner",
      selector: (row) => row?.ownerName,
      cell: (row) => row?.ownerName || "N/A",
    },
    {
      name: "OwnerShip Type",
      selector: (row) => row?.ownerShipType?.name,
      cell: (row) => row?.ownerShipType?.name || "N/A",
    },
    {
      name: "Vehicle Type",
      selector: (row) => row?.vehicle?.name,
      cell: (row) => row?.vehicle?.name || "N/A",
    },
    {
      name: "Issue Date",
      selector: (row) => localizeDateShort(row?.issueDate),
      cell: (row) => localizeDateShort(row?.issueDate) || "N/A",
    },
    {
      name: "Expiry Date",
      selector: (row) => localizeDateShort(row?.expiryDate),
      cell: (row) => localizeDateShort(row?.expiryDate) || "N/A",
    },
    {
      name: "Attachments",
      selector: (row) => (row?.attachments?.length > 0 ? row?.attachments : []),
      cell: (row) =>
        row?.attachments?.length > 0 && (
          <div className="flex gap-3">
            {row?.attachments?.map((attachment) => (
              <a
                key={attachment?._id}
                href={attachment?.url}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--primary)" }}
              >
                <IconButton
                  size="small"
                  aria-label="View Attachment"
                  onClick={() => window.open(attachment?.url, "_blank")}
                >
                  <Visibility />
                </IconButton>
              </a>
            ))}
          </div>
        ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <IconButton
            size="small"
            aria-label="Edit Registration"
            onClick={() => {
              setSelectedRegistration(row);
              setShowRegistrationForm(true);
            }}
          >
            <ModeEditOutlineTwoTone />
          </IconButton>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [updateRegistration, { isLoading: updatingRegistration }] =
    useUpdateVehicleRegistrationMutation();

  const [fetchRegistrations, { data: registrationRes, isLoading, isFetching }] =
    useLazyListVehicleRegistrationsQuery();

  // Methods
  const handleCancelRegistration = () => {
    setShowRegistrationForm(false);
    setSelectedRegistration(null);
  };

  const handleUpdateRegistration = async (data) => {
    try {
      await updateRegistration({
        vehicleId: vehicleDetail?._id,
        registrationId: selectedRegistration?._id,
        ...data,
      }).unwrap();
      toast.success("Registration updated successfully!");
      handleCancelRegistration();
    } catch (err) {
      toast.error(err?.data?.message ?? "Failed to update registration!");
    }
  };

  React.useEffect(() => {
    if (!vehicleDetail?._id) return;
    fetchRegistrations({
      vehicleId: vehicleDetail?._id,
      page: currentPage,
      limit: perPage,
    });
  }, [
    vehicleDetail?._id,
    currentPage,
    registrationRes?.totalItems,
    fetchRegistrations,
    perPage,
  ]);

  return (
    <>
      <BreadcrumbLayout.Paper>
        {registrationRes ? (
          <AppTable
            columns={columns}
            data={registrationRes?.data ?? []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={registrationRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={2}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" marginLeft={1}>
              Loading Vehicle Registrations...
            </Typography>
          </Box>
        )}
      </BreadcrumbLayout.Paper>
      <FormDrawer
        title={selectedRegistration ? "Edit Registration" : "Add Registration"}
        open={showRegistrationForm}
        onClose={handleCancelRegistration}
      >
        <VehicleRegistrationForm
          isBusy={updatingRegistration}
          vehicleRegistration={selectedRegistration}
          onSubmit={handleUpdateRegistration}
          onCancel={handleCancelRegistration}
        />
      </FormDrawer>
    </>
  );
};

export default VehicleRegistrationList;
